import {
  DefaultOptions,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import * as React from "react"

import { ServerContextValue, ServerProviderProps } from "./ServerContext.types"
import { ClientLogger } from "@mimber/schema"
import createServer from "./server"
import serverConfig from "./serverConfig"
import { normalizeDomain, shouldDehydrateQuery } from "./ServerContext.utils"

export const ServerContext = React.createContext<
  ServerContextValue | undefined
>(undefined)

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000
export const PERSISTED_CACHE_MAX_AGE_MS = 8 * 60 * 60 * 1000
const DEFAULT_QUERY_OPTIONS: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    staleTime: FIFTEEN_MINUTES_MS,
    gcTime: PERSISTED_CACHE_MAX_AGE_MS,
    retry: 1,
  },
}

const buildDefaultOptions = (
  queryClientOptions?: DefaultOptions
): DefaultOptions => ({
  ...DEFAULT_QUERY_OPTIONS,
  ...queryClientOptions,
  queries: {
    ...DEFAULT_QUERY_OPTIONS.queries,
    ...queryClientOptions?.queries,
  },
  mutations: {
    ...queryClientOptions?.mutations,
  },
})

export let PCLogger: ClientLogger = {
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
}

export const ServerProvider: React.FunctionComponent<ServerProviderProps> = ({
  acquireTurnstileToken,
  appAgent = "Achievement",
  cacheBusterVersion,
  children,
  debug = false,
  deviceId,
  environment = "development",
  customServerConfig,
  getAccessToken = () => Promise.resolve("portal-token"),
  getOrganizationId,
  logger,
  queryClient: providedQueryClient,
  persister,
  platform,
  queryClientOptions,
  version = 1,
}) => {
  const queryClient = React.useState(
    () =>
      providedQueryClient ??
      new QueryClient({
        defaultOptions: buildDefaultOptions(queryClientOptions),
      })
  )[0]

  // Wipe stale in-memory data before the persister restores a different
  // user's cache, without remounting the tree below this provider.
  const previousCacheBusterRef = React.useRef(cacheBusterVersion)
  React.useEffect(() => {
    if (providedQueryClient) {
      return
    }

    if (previousCacheBusterRef.current !== cacheBusterVersion) {
      previousCacheBusterRef.current = cacheBusterVersion
      queryClient.clear()
    }
  }, [cacheBusterVersion, providedQueryClient, queryClient])

  const contextValue = React.useMemo(() => {
    const activeServerConfig = customServerConfig ?? serverConfig[environment]

    const normalizedDomain = normalizeDomain(
      activeServerConfig.domain,
      platform
    )

    const server = createServer({
      appAgent,
      axiosRequestConfig: {
        baseURL: `${normalizedDomain}api/v${version}/`,
        withCredentials: true,
        headers: deviceId
          ? {
              "device-id": deviceId,
            }
          : undefined,
      },
      debug,
      getAuthorization: async () => `Bearer ${await getAccessToken()}`,
      getOrganizationId,
    })

    const unauthenticatedServer = createServer({
      appAgent,
      axiosRequestConfig: {
        baseURL: `${normalizedDomain}api/v${version}/`,
      },
      debug,
    })

    return { server, unauthenticatedServer, acquireTurnstileToken }
  }, [
    acquireTurnstileToken,
    customServerConfig,
    environment,
    appAgent,
    version,
    deviceId,
    debug,
    getAccessToken,
    getOrganizationId,
    platform,
  ])

  React.useEffect(() => {
    if (logger) {
      PCLogger = logger
    }
  }, [logger])

  React.useEffect(() => {
    queryClient.setDefaultOptions(buildDefaultOptions(queryClientOptions))
  }, [queryClient, queryClientOptions])

  const content = (
    <ServerContext.Provider value={contextValue}>
      {children}
      {typeof document !== "undefined" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </ServerContext.Provider>
  )

  if (persister) {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: PERSISTED_CACHE_MAX_AGE_MS,
          buster: cacheBusterVersion,
          dehydrateOptions: {
            shouldDehydrateQuery,
            // Never persist mutation results (e.g. Stripe client secrets).
            shouldDehydrateMutation: () => false,
          },
        }}
      >
        {content}
      </PersistQueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>
  )
}

export const useServerContext = () => {
  const context = React.useContext(ServerContext)

  if (!context) {
    throw new Error("useServerContext must be used within ServerProvider")
  }

  return context
}
